import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { PDFFile } from '@adornis/pdf/db/pdf-file.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import JSZip from 'jszip';
import { readFile } from 'node:fs/promises';
import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFImage } from 'pdf-lib';
import { OWL_PNG_BASE64 } from './pdf-logo-data.js';
import { GRADING_THRESHOLDS } from '../../shared/constants.js';
import { averageNumericGrade, gradeFromPercentage, parseGradingScale } from '../../shared/grading-scales.js';
import { applyGradeStepDeduction } from '../../shared/grammar-grading.js';
import {
  formatTaskReferenceForDisplay,
  getSoloLanguageUseRule,
  resolveLanguageUseGroupPoints,
  resolveSoloLanguageUseMaxPoints,
  resolveSoloLanguageUsePoints,
} from '../../shared/grammar-rules-resolution.js';
import { Exam } from '../exam.js';
import { Submission } from '../submission.js';
import { awardedPoints, computeAggregate, ownMaxPoints } from '../utils/points-model.js';

type MaybeNumber = number | string | null | undefined;

/** A single LanguageTool-detected mistake, task-tagged so the consolidated Fehlerindex section
 * (see buildSubmissionReportPDFBytes) can list every error regardless of which task it came from. */
interface ReportGrammarError {
  message: string;
  category: string;
  context: string;
}

/** Own-criteria language-use rule (case 2) for a single leaf task — rendered as its own pseudo-task
 * section ("<Aufgabe> · Sprachliche Leistung"), mirroring the dedicated "· Sprachliche Leistung" column
 * in the summary canvas's StudentsOverviewTab, rather than folded into the content task's own section.
 * The detected error list lives on ReportTaskRow.grammarErrors instead (shared with group/FI-only
 * tasks), not here. */
interface ReportOwnLanguageUseInfo {
  points: number;
  maxPoints: number;
  criteria: string;
  explanation: string;
}

interface ReportTaskRow {
  label: string;
  description: string;
  studentAnswer: string;
  gradingScheme: string;
  masterSolution: string;
  points: number;
  maxPoints: number;
  explanation: string;
  imageIds: string[];
  /** Own-criteria language-use rule (case 2) for this leaf task, or null when it has no merged
   * languageUseCriteria. */
  ownLanguageUse: ReportOwnLanguageUseInfo | null;
  /** Detected grammar/spelling errors for this task (own-criteria, group, or FI-fallback alike — the
   * detection step runs for every grammar-eligible task regardless of scoring mode). Empty when this
   * task was never grammar-evaluated. Feeds the consolidated Fehlerindex section, not this task's own
   * section — the aggregate word/error counts backing the FI grade deduction come from
   * Submission.grammarGradingSummary instead (see buildSubmissionReportPDFBytes). */
  grammarErrors: ReportGrammarError[];
}

interface TaskDefinition {
  taskKey: string;
  label: string;
  maxPoints: number;
  /** Own-criteria language-use max points across this task's subtree — 0 when no node in the subtree
   * carries own languageUseCriteria. Gate the companion "· Sprachliche Leistung" column's visibility on
   * hasOwnLanguageUseCriteria below, not on this number (mirrors utils.ts on the client). */
  languageUseMaxPoints: number;
  hasOwnLanguageUseCriteria: boolean;
}

/** One column per distinct multi-task LanguageUseScheme group, independent of the task tree. */
interface LanguageUseGroupColumn {
  schemeId: string;
  label: string;
  maxPoints: number;
  dependsOn: string[];
}

interface TeacherStudentSummary {
  id: string;
  submissionIndex: number;
  name: string;
  pointsByTask: Record<string, number>;
  confidenceByTask: Record<string, number | null>;
  isApprovedByTask: Record<string, boolean>;
  hasManualApprovalByTask: Record<string, boolean>;
  /** Whether any own-criteria or group language-use grading is still unresolved (low/medium
   * confidence, not manually approved) — the language-use counterpart to isApprovedByTask, gating
   * report generation the same way content confidence does. */
  hasOpenLanguageUseClarification: boolean;
  /** Own-criteria language-use points earned per task (keyed by taskKey), meaningful only where the
   * matching TaskDefinition.hasOwnLanguageUseCriteria is true. */
  ownLanguageUsePointsByTask: Record<string, number>;
  /** Earned points per multi-task LanguageUseScheme group, keyed by LanguageUseScheme._id. */
  languageUseGroupPointsBySchemeId: Record<string, number>;
  /** Whether the Fehlerindex grade deduction is actually active for this submission — sourced
   * straight from the persisted Submission.grammarGradingSummary (written by
   * persistGrammarFiOutcomes.ts, which is the only place that knows the teacher's FI-switch state),
   * rather than re-derived here from task types/rules. */
  fiApplied: boolean;
  /** Error/word counts and aggregate Fehlerindex backing the grade deduction — null unless fiApplied. */
  fiErrorCount: number | null;
  fiWordCount: number | null;
  fiFehlerindex: number | null;
  totalPoints: number;
  totalMaxPoints: number;
  percentage: number;
  /** Final grade including a possible Fehlerindex deduction (equals baseGrade when none applies). */
  grade: string;
  /** Pure points-derived grade before any Fehlerindex deduction. */
  baseGrade: string;
  /** Grade steps deducted via the persisted grammar FI summary (0 when FI is off / rules mode). */
  fiDeductionSteps: number;
}

/** Applied FI grade-step deduction from the persisted Submission.grammarGradingSummary (0 if none). */
function resolveFiDeductionSteps(submission: any): number {
  const grammarSummary = unwrapDoc<any>(submission?.grammarGradingSummary);
  if (!grammarSummary?.fiApplied) return 0;
  const steps = Number(grammarSummary.deductionSteps);
  return Number.isFinite(steps) && steps > 0 ? Math.round(steps) : 0;
}

function parseNumber(value: MaybeNumber): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== 'string') return null;
  const parsed = Number(value.replace(',', '.').replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
}

function unwrapDoc<T>(value: T | { doc?: T } | null | undefined): T | null {
  if (!value) return null;
  if (typeof value === 'object' && 'doc' in (value as any)) {
    return ((value as any).doc as T) ?? null;
  }
  return value as T;
}

function normalizeText(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.replace(/\s+/g, ' ').trim();
}

// Like normalizeText but preserves newlines so Markdown/LaTeX can be rendered.
function preserveNewlines(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.replace(/[ \t]+/g, ' ').trim();
}

/** Flattens a GrammarEvaluation's detected errors (LanguageTool matches) into a display-ready list. */
function extractGrammarErrors(grammarEvaluation: any): ReportGrammarError[] {
  const errors = Array.isArray(grammarEvaluation?.errors) ? grammarEvaluation.errors : [];
  return errors.map((error: any) => ({
    // `message` carries the actual explanation of what's wrong; `shortMessage` is often just a terse
    // category label (or empty), so it's only a fallback, not the primary source.
    message: normalizeText(error?.message) || normalizeText(error?.shortMessage) || 'Fehler',
    category: normalizeText(error?.category),
    context: normalizeText(error?.context),
  }));
}

function parseConfidenceRatio(value: unknown): number | null {
  const parsed = parseNumber(value as MaybeNumber);
  if (parsed == null) return null;
  if (parsed >= 0 && parsed <= 1) return parsed;
  return parsed / 100;
}

function toPdfSafeText(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u2070\u2074\u2075\u2076\u2077\u2078\u2079]/g, match => {
      const map: Record<string, string> = {
        '\u2070': '0',
        '\u2074': '4',
        '\u2075': '5',
        '\u2076': '6',
        '\u2077': '7',
        '\u2078': '8',
        '\u2079': '9',
      };
      return map[match] ?? '';
    })
    .replace(/[\u00B2]/g, '2')
    .replace(/[\u00B3]/g, '3')
    .replace(/[\u00B9]/g, '1')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2026]/g, '...')
    .replace(/[^\x09\x0A\x0D\x20-\xFF]/g, '');
}

// ─── Rich-text pipeline ──────────────────────────────────────────────────────

type LineStyle = 'h1' | 'h2' | 'h3' | 'body' | 'code' | 'bullet' | 'blockquote';
interface RichLine {
  text: string;
  style: LineStyle;
}

// Convert LaTeX commands to Latin-1 safe / ASCII text so Helvetica can render it.
function latexToSafeText(text: string): string {
  if (!text) return text;
  return (
    text
      .replace(/\\begin\{[^}]+\}/g, '')
      .replace(/\\end\{[^}]+\}/g, '')
      .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '($1)/($2)')
      .replace(/\\sqrt\{([^}]+)\}/g, 'sqrt($1)')
      .replace(/\\sqrt/g, 'sqrt')
      .replace(/\^\{([^}]+)\}/g, '^($1)')
      .replace(/\^2/g, '²')
      .replace(/\^3/g, '³')
      .replace(/\_\{([^}]+)\}/g, '_($1)')
      // Greek letters → ASCII names
      .replace(/\\alpha/g, 'alpha')
      .replace(/\\beta/g, 'beta')
      .replace(/\\gamma/g, 'gamma')
      .replace(/\\Gamma/g, 'Gamma')
      .replace(/\\delta/g, 'delta')
      .replace(/\\Delta/g, 'Delta')
      .replace(/\\epsilon/g, 'eps')
      .replace(/\\varepsilon/g, 'eps')
      .replace(/\\zeta/g, 'zeta')
      .replace(/\\eta/g, 'eta')
      .replace(/\\theta/g, 'theta')
      .replace(/\\Theta/g, 'Theta')
      .replace(/\\kappa/g, 'kappa')
      .replace(/\\lambda/g, 'lambda')
      .replace(/\\Lambda/g, 'Lambda')
      .replace(/\\mu/g, 'mu')
      .replace(/\\nu/g, 'nu')
      .replace(/\\xi/g, 'xi')
      .replace(/\\Xi/g, 'Xi')
      .replace(/\\pi/g, 'pi')
      .replace(/\\Pi/g, 'Pi')
      .replace(/\\rho/g, 'rho')
      .replace(/\\sigma/g, 'sigma')
      .replace(/\\Sigma/g, 'Sigma')
      .replace(/\\tau/g, 'tau')
      .replace(/\\phi/g, 'phi')
      .replace(/\\Phi/g, 'Phi')
      .replace(/\\omega/g, 'omega')
      .replace(/\\Omega/g, 'Omega')
      // Operators – use Latin-1 where available
      .replace(/\\times/g, '×')
      .replace(/\\div/g, '÷')
      .replace(/\\pm/g, '±')
      .replace(/\\cdot/g, '·')
      .replace(/\\leq/g, '<=')
      .replace(/\\geq/g, '>=')
      .replace(/\\neq/g, '!=')
      .replace(/\\approx/g, '~=')
      .replace(/\\equiv/g, '===')
      .replace(/\\infty/g, 'inf')
      .replace(/\\sum/g, 'sum')
      .replace(/\\prod/g, 'prod')
      .replace(/\\int/g, 'int')
      .replace(/\\partial/g, 'del')
      .replace(/\\nabla/g, 'grad')
      .replace(/\\forall/g, 'forall')
      .replace(/\\exists/g, 'exists')
      .replace(/\\notin/g, ' not in ')
      .replace(/\\in\b/g, ' in ')
      .replace(/\\subset/g, 'subset')
      .replace(/\\to\b/g, '->')
      .replace(/\\rightarrow/g, '->')
      .replace(/\\leftarrow/g, '<-')
      .replace(/\\Rightarrow/g, '=>')
      .replace(/\\Leftrightarrow/g, '<=>')
      .replace(/\\neg/g, 'not ')
      .replace(/\\land/g, ' and ')
      .replace(/\\lor/g, ' or ')
      // Strip remaining commands (keep arg content)
      .replace(/\\[a-zA-Z]+\{([^}]*)\}/g, '$1')
      .replace(/\\[a-zA-Z]+/g, '')
      // Strip math delimiters and braces
      .replace(/\$\$/g, '')
      .replace(/\$/g, '')
      .replace(/\{|\}/g, '')
  );
}

function cleanInline(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/!\[[^\]]*\]\([^)]+\)/g, '[Bild]');
}

function parseMarkdownLines(text: string): RichLine[] {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const result: RichLine[] = [];
  let inCodeBlock = false;
  for (const line of lines) {
    const t = line.trimEnd();
    if (t.startsWith('```')) {
      inCodeBlock = !inCodeBlock;
      continue;
    }
    if (inCodeBlock) {
      result.push({ text: t, style: 'code' });
      continue;
    }
    if (t.startsWith('### ')) {
      result.push({ text: cleanInline(t.slice(4)), style: 'h3' });
    } else if (t.startsWith('## ')) {
      result.push({ text: cleanInline(t.slice(3)), style: 'h2' });
    } else if (t.startsWith('# ')) {
      result.push({ text: cleanInline(t.slice(2)), style: 'h1' });
    } else if (/^[-*+]\s/.test(t)) {
      result.push({ text: cleanInline(t.replace(/^[-*+]\s+/, '')), style: 'bullet' });
    } else if (/^\d+\.\s/.test(t)) {
      result.push({ text: cleanInline(t), style: 'bullet' });
    } else if (t.startsWith('> ')) {
      result.push({ text: cleanInline(t.slice(2)), style: 'blockquote' });
    } else {
      result.push({ text: cleanInline(t), style: 'body' });
    }
  }
  return result;
}

async function fetchTaskImages(imageIds: string[]): Promise<Array<{ bytes: Buffer; isPng: boolean }>> {
  if (!imageIds.length) return [];
  const results: Array<{ bytes: Buffer; isPng: boolean }> = [];
  const fileCollection = await getCollection(AdornisFile);
  for (const id of imageIds) {
    try {
      const file = (await fileCollection.findOne({ _id: id })) as AdornisFile | null;
      if (!file) continue;
      const filePath = file.getFullPath();
      if (!filePath) continue;
      const raw = await readFile(filePath);
      const isPng = file.meta?.mimetype === 'image/png' || filePath.toLowerCase().endsWith('.png');
      results.push({ bytes: Buffer.from(raw), isPng });
    } catch {
      /* skip unreadable images */
    }
  }
  return results;
}

// ─── End rich-text pipeline ───────────────────────────────────────────────────

function resolveMaxPoints(task: any): number {
  const resolveNodeMaxPoints = (node: any): number => {
    if (!node) return 0;

    const ownRaw =
      parseNumber(unwrapDoc<any>(node?.gradingScheme)?.maxPoints) ??
      parseNumber(unwrapDoc<any>(node?.overallGradingScheme)?.maxPoints) ??
      parseNumber(node?.maxPoints) ??
      1;
    const own = node?.needsAnswer !== false ? Math.max(0, ownRaw) : 0;

    const children = Array.isArray(node?.subTasks) ? node.subTasks : [];
    const childrenSum = children.reduce((sum: number, child: any) => sum + resolveNodeMaxPoints(child), 0);

    return own + childrenSum;
  };

  return resolveNodeMaxPoints(task);
}

/**
 * Total max points for language-use rules that count toward the exam grade but aren't part of the
 * content-only pointsByTask breakdown: single-task merged languageUseCriteria (walked across the whole
 * task tree) plus every multi-task LanguageUseScheme group. Exam-wide (same for every student).
 */
function sumLanguageUseMaxPoints(exam: any): number {
  let maxPoints = 0;
  const languageUseSchemeRules = Array.isArray(exam?.languageUseSchemeRules) ? exam.languageUseSchemeRules : [];
  const walk = (node: any, path: string[]) => {
    const solo = resolveSoloLanguageUseMaxPoints(path.join('|'), languageUseSchemeRules);
    if (solo != null) maxPoints += solo;
    (Array.isArray(node?.subTasks) ? node.subTasks : []).forEach((child: any) =>
      walk(child, [...path, String(child?.taskNumber ?? '')]),
    );
  };
  (Array.isArray(exam?.tasks) ? exam.tasks : []).forEach((task: any) => walk(task, [String(task?.taskNumber ?? '')]));
  // Solo (1-member) rules are already counted above via the per-task walk — only multi-member groups
  // are summed here, to avoid double-counting.
  maxPoints += languageUseSchemeRules
    .filter((scheme: any) => Array.isArray(scheme?.dependsOn) && scheme.dependsOn.length > 1)
    .reduce((sum: number, scheme: any) => sum + Math.max(0, parseNumber(scheme?.maxPoints) ?? 0), 0);
  return maxPoints;
}

/**
 * Per-student counterpart to sumLanguageUseMaxPoints: the language-use points this submission
 * actually earned. Every LanguageUseScheme's result (solo or multi-member) lives in
 * Submission.languageUseRuleResults, so a flat scan of that array covers all of them — no need to
 * walk the task tree.
 */
function sumLanguageUseEarnedPoints(exam: any, submission: any): number {
  let points = 0;
  const groupResults = Array.isArray(submission?.languageUseRuleResults) ? submission.languageUseRuleResults : [];
  const schemes = Array.isArray(exam?.languageUseSchemeRules) ? exam.languageUseSchemeRules : [];
  for (const result of groupResults) {
    const scheme = schemes.find((s: any) => String(s?._id ?? '') === String(result?.languageUseSchemeId ?? ''));
    if (!scheme) continue;
    points += resolveLanguageUseGroupPoints(scheme, result).earnedPoints;
  }
  return points;
}

/**
 * Per-student counterpart to aggregateTaskTree's content-only hasOpenClarification: whether any
 * LanguageUseScheme result (solo or multi-member, Submission.languageUseRuleResults[].confidence/isApproved)
 * is still below the confidence threshold and not manually approved.
 */
function hasSubmissionOpenLanguageUseClarification(exam: any, submission: any): boolean {
  const groupResults = Array.isArray(submission?.languageUseRuleResults) ? submission.languageUseRuleResults : [];
  const schemes = Array.isArray(exam?.languageUseSchemeRules) ? exam.languageUseSchemeRules : [];
  return groupResults.some((result: any) => {
    const scheme = schemes.find((s: any) => String(s?._id ?? '') === String(result?.languageUseSchemeId ?? ''));
    if (!scheme) return false;
    const confidence = parseConfidenceRatio(result?.confidence);
    const approved = result?.isApproved === true;
    return confidence != null && confidence < GRADING_THRESHOLDS.MEDIUM_CONFIDENCE && !approved;
  });
}

/** Solo-rule language-use max points across a single task's whole subtree — 0 when no node in the
 * subtree carries a solo language-use rule. Per-task counterpart to sumLanguageUseMaxPoints. */
function taskSoloLanguageUseMaxPoints(task: any, languageUseSchemeRules: any[]): number {
  let total = 0;
  const walk = (node: any, path: string[]) => {
    const solo = resolveSoloLanguageUseMaxPoints(path.join('|'), languageUseSchemeRules);
    if (solo != null) total += solo;
    (Array.isArray(node?.subTasks) ? node.subTasks : []).forEach((child: any) =>
      walk(child, [...path, String(child?.taskNumber ?? '')]),
    );
  };
  walk(task, [String(task?.taskNumber ?? '')]);
  return total;
}

/** Whether any node in a task's subtree carries a solo (1-member) language-use rule, independent of
 * languageUseMaxPoints — the correct gate for the "· Sprachliche Leistung" column's visibility. */
function taskHasSoloLanguageUseCriteria(task: any, languageUseSchemeRules: any[]): boolean {
  const check = (node: any, path: string[]): boolean => {
    if (getSoloLanguageUseRule(path.join('|'), languageUseSchemeRules)) return true;
    return (Array.isArray(node?.subTasks) ? node.subTasks : []).some((child: any) =>
      check(child, [...path, String(child?.taskNumber ?? '')]),
    );
  };
  return check(task, [String(task?.taskNumber ?? '')]);
}

/** Solo-rule language-use points earned across a single task's whole subtree. Per-task counterpart to
 * sumLanguageUseEarnedPoints's solo-rule walk. */
function taskSoloLanguageUseEarnedPoints(
  examNode: any,
  submissionNode: any,
  languageUseSchemeRules: any[],
  languageUseRuleResults: any[],
  path: string[],
): number {
  if (!examNode || !submissionNode) return 0;
  const solo = resolveSoloLanguageUsePoints(path.join('|'), languageUseSchemeRules, languageUseRuleResults);
  let total = solo ? solo.earnedPoints : 0;
  const examChildren = Array.isArray(examNode?.subTasks) ? examNode.subTasks : [];
  const submissionChildren = Array.isArray(submissionNode?.subTasksSubmission)
    ? submissionNode.subTasksSubmission
    : [];
  submissionChildren.forEach((child: any, childIndex: number) => {
    const examChild =
      examChildren.find((c: any) => String(c?.taskNumber ?? '') === String(child?.taskNumber ?? '')) ??
      examChildren[childIndex];
    total += taskSoloLanguageUseEarnedPoints(
      examChild,
      child,
      languageUseSchemeRules,
      languageUseRuleResults,
      [...path, String(child?.taskNumber ?? '')],
    );
  });
  return total;
}

/** One column per distinct multi-task LanguageUseScheme group, ordered by each group's lowest linked
 * task number for predictable teacher-facing ordering — mirrors the client's languageUseGroupColumns.
 * Solo (1-member) rules are excluded — those render as a "· Sprachliche Leistung" pseudo-task row instead. */
function buildLanguageUseGroupColumns(exam: any): LanguageUseGroupColumn[] {
  const schemes: any[] = (Array.isArray(exam?.languageUseSchemeRules) ? exam.languageUseSchemeRules : []).filter(
    (scheme: any) => Array.isArray(scheme?.dependsOn) && scheme.dependsOn.length > 1,
  );
  const lowestDependsOnTaskNumber = (dependsOn: string[]): number => {
    // dependsOn entries are "|"-joined qualified paths (e.g. "2|a") — sort by the top-level task
    // number (the first segment), not the whole string.
    const numeric = dependsOn.map(tn => Number(tn.split('|')[0])).filter(n => Number.isFinite(n));
    return numeric.length > 0 ? Math.min(...numeric) : Number.POSITIVE_INFINITY;
  };
  return schemes
    .map((scheme: any) => {
      const dependsOn = Array.isArray(scheme?.dependsOn) ? scheme.dependsOn.map(String) : [];
      return {
        schemeId: String(scheme?._id ?? ''),
        label: `Gruppe: Aufgabe ${dependsOn.map(formatTaskReferenceForDisplay).join(', ')}`,
        maxPoints: Math.max(0, parseNumber(scheme?.maxPoints) ?? 0),
        dependsOn,
      };
    })
    .sort((a, b) => lowestDependsOnTaskNumber(a.dependsOn) - lowestDependsOnTaskNumber(b.dependsOn));
}

type LanguageUseAwareColumn =
  | { kind: 'task'; taskKey: string; label: string; maxPoints: number }
  | { kind: 'ownLanguageUse'; taskKey: string; label: string; maxPoints: number }
  | { kind: 'group'; schemeId: string; label: string; maxPoints: number; dependsOn: string[] }
  | { kind: 'fehlerindex' };

/** Flat, ordered column list combining content tasks, their own-criteria language-use companions,
 * every language-use-group column, and the Fehlerindex column — the single source of truth for the CSV
 * and the teacher PDF table, so both stay in sync with what the summary canvas's StudentsOverviewTab
 * shows. */
function buildLanguageUseAwareColumns(summary: {
  activeTaskDefinitions: TaskDefinition[];
  languageUseGroupColumns: LanguageUseGroupColumn[];
  hasFiApplied: boolean;
}): LanguageUseAwareColumn[] {
  const columns: LanguageUseAwareColumn[] = [];
  for (const t of summary.activeTaskDefinitions) {
    columns.push({ kind: 'task', taskKey: t.taskKey, label: t.label, maxPoints: t.maxPoints });
    if (t.hasOwnLanguageUseCriteria) {
      columns.push({
        kind: 'ownLanguageUse',
        taskKey: t.taskKey,
        label: `${t.label} · Sprachliche Leistung`,
        maxPoints: t.languageUseMaxPoints,
      });
    }
  }
  for (const g of summary.languageUseGroupColumns) {
    columns.push({ kind: 'group', schemeId: g.schemeId, label: g.label, maxPoints: g.maxPoints, dependsOn: g.dependsOn });
  }
  if (summary.hasFiApplied) columns.push({ kind: 'fehlerindex' });
  return columns;
}

function resolveOwnNodeMaxPoints(task: any): number {
  const directMax =
    parseNumber(unwrapDoc<any>(task?.gradingScheme)?.maxPoints) ??
    parseNumber(unwrapDoc<any>(task?.overallGradingScheme)?.maxPoints) ??
    parseNumber(task?.maxPoints) ??
    1;
  return Math.max(0, directMax);
}

function parseScoreFromExplanation(text: unknown): number | null {
  if (typeof text !== 'string' || text.trim().length === 0) return null;
  const normalized = text.replace(',', '.');
  const patterns = [
    /punkte\s*[:=]?\s*\*{0,2}\s*([0-9]+(?:\.[0-9]+)?)/i,
    /([0-9]+(?:\.[0-9]+)?)\s*\/\s*[0-9]+(?:\.[0-9]+)?\s*punkte?/i,
    /([0-9]+(?:\.[0-9]+)?)\s+von\s+[0-9]+(?:\.[0-9]+)?\s+punkten?/i,
  ];

  for (const pattern of patterns) {
    const match = normalized.match(pattern);
    if (!match?.[1]) continue;
    const parsed = Number(match[1]);
    if (Number.isFinite(parsed)) return parsed;
  }

  return null;
}

function resolvePoints(gradingResult: any, maxPoints: number): number {
  const resolved = unwrapDoc<any>(gradingResult);
  const points =
    parseNumber(resolved?.actualPointsGiven) ??
    parseScoreFromExplanation(resolved?.explanation) ??
    parseNumber(resolved?.score) ??
    0;
  if (!Number.isFinite(points)) return 0;
  return Math.max(0, Math.min(points, Math.max(0, maxPoints)));
}

function roundToOne(value: number): number {
  return Math.round(value * 10) / 10;
}

function wrapText(text: string, font: PDFFont, fontSize: number, maxWidth: number): string[] {
  const normalized = toPdfSafeText(text).replace(/\r\n/g, '\n');
  const paragraphs = normalized.split('\n');
  const lines: string[] = [];

  for (const paragraph of paragraphs) {
    const trimmed = paragraph.trim();
    if (!trimmed) {
      lines.push('');
      continue;
    }

    const words = trimmed.split(/\s+/);
    let currentLine = '';

    for (const word of words) {
      const candidate = currentLine ? `${currentLine} ${word}` : word;
      const width = font.widthOfTextAtSize(candidate, fontSize);

      if (width <= maxWidth || !currentLine) {
        currentLine = candidate;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }
  }

  return lines.length > 0 ? lines : [''];
}

/**
 * Exported for the result-distribution snapshot (db/api/distribute-exam-results.ts), which needs exactly
 * this per-task walk. The rows carry markdown/LaTeX as authored — the PDF path flattens it at render time
 * (`latexToSafeText`), not here — so they can be handed to a markdown renderer unchanged.
 *
 * Callers publishing to STUDENTS must drop `gradingScheme` and `masterSolution`: both are teacher-internal.
 */
export function createSubmissionTaskRows(exam: any, submission: any): ReportTaskRow[] {
  const examTasks = Array.isArray(exam?.tasks) ? exam.tasks : [];
  const submissionTasks = Array.isArray(submission?.taskSubmissions) ? submission.taskSubmissions : [];
  const languageUseSchemeRules = Array.isArray(exam?.languageUseSchemeRules) ? exam.languageUseSchemeRules : [];
  const rows: ReportTaskRow[] = [];

  const resolveExamChildForSubmissionChild = (examParent: any, submissionChild: any, childIndex: number): any => {
    const examChildren = Array.isArray(examParent?.subTasks) ? examParent.subTasks : [];
    const byTaskNumber = examChildren.find(
      (examChild: any) => String(examChild?.taskNumber ?? '') === String(submissionChild?.taskNumber ?? ''),
    );
    return byTaskNumber ?? examChildren[childIndex] ?? null;
  };

  const pushLeafRows = (submissionNode: any, examNode: any, path: string[]) => {
    if (!submissionNode) return;

    // A hybrid answerable parent (needsAnswer: true with its own gradingResult, e.g. "1.1" combining an
    // essay question with lettered subtasks) needs its OWN row here — having children doesn't mean it
    // has nothing of its own to report. Only a pure task frame (needsAnswer: false) has no own row.
    const nodeNeedsAnswer = examNode?.needsAnswer !== false;
    if (nodeNeedsAnswer) {
      const label =
        path.length > 0
          ? `Aufgabe ${path[0]}${path.length > 1 ? ` - Teilaufgabe ${path.slice(1).join(' > ')}` : ''}`
          : 'Aufgabe';

      const maxPoints = ownMaxPoints(examNode);

      // Solo language-use rule: rendered as its own "<Aufgabe> · Sprachliche Leistung" pseudo-task —
      // needs the criteria text and earned points, but not the error list (that's collected below,
      // ungated, for the consolidated Fehlerindex section at the end of the report).
      const qualifiedTaskNumber = path.join('|');
      const ownLanguageUseCriteria = getSoloLanguageUseRule(qualifiedTaskNumber, languageUseSchemeRules);
      const ownLanguageUseResult = ownLanguageUseCriteria
        ? (submission?.languageUseRuleResults ?? []).find(
            (r: any) => String(r?.languageUseSchemeId ?? '') === ownLanguageUseCriteria.schemeId,
          )
        : undefined;
      const ownLanguageUsePoints = resolveSoloLanguageUsePoints(
        qualifiedTaskNumber,
        languageUseSchemeRules,
        submission?.languageUseRuleResults,
      );
      const ownLanguageUse: ReportOwnLanguageUseInfo | null = ownLanguageUseCriteria
        ? {
            points: ownLanguageUsePoints ? ownLanguageUsePoints.earnedPoints : 0,
            maxPoints: ownLanguageUseCriteria.maxPoints,
            criteria: preserveNewlines(ownLanguageUseCriteria.criteria),
            explanation: preserveNewlines(ownLanguageUseResult?.explanation),
          }
        : null;

      // Detected grammar/spelling errors (LanguageTool) are populated for every grammar-eligible task
      // regardless of whether it's scored via own criteria, a group, or the Fehlerindex fallback — read
      // unconditionally here so the consolidated Fehlerindex section can list all of them, tagged by task.
      const grammarErrors = extractGrammarErrors(unwrapDoc<any>(submissionNode?.grammarEvaluation));

      rows.push({
        label,
        description: preserveNewlines(examNode?.description),
        studentAnswer: preserveNewlines(submissionNode?.studentAnswer),
        gradingScheme: preserveNewlines(unwrapDoc<any>(examNode?.gradingScheme)?.criteria),
        masterSolution: preserveNewlines(unwrapDoc<any>(examNode?.masterSolution)?.content),
        points: Math.max(0, Math.min(awardedPoints(submissionNode), maxPoints)),
        maxPoints,
        explanation: preserveNewlines(unwrapDoc<any>(submissionNode?.gradingResult)?.explanation),
        imageIds: Array.isArray(submissionNode?.imageIds) ? (submissionNode.imageIds as string[]) : [],
        ownLanguageUse,
        grammarErrors,
      });
    }

    const submissionChildren = Array.isArray(submissionNode?.subTasksSubmission)
      ? submissionNode.subTasksSubmission
      : [];
    submissionChildren.forEach((child: any, childIndex: number) => {
      const examChild = resolveExamChildForSubmissionChild(examNode, child, childIndex);
      const childTaskNumber = String(child?.taskNumber ?? childIndex + 1);
      pushLeafRows(child, examChild, [...path, childTaskNumber]);
    });
  };

  for (let mainTaskIndex = 0; mainTaskIndex < submissionTasks.length; mainTaskIndex += 1) {
    const submissionMainTask = submissionTasks[mainTaskIndex];
    if (!submissionMainTask) continue;

    const taskNumber = String(submissionMainTask.taskNumber ?? mainTaskIndex + 1);
    const examMainTask =
      examTasks.find((task: any) => String(task?.taskNumber ?? '') === taskNumber) ?? examTasks[mainTaskIndex] ?? null;

    pushLeafRows(submissionMainTask, examMainTask, [taskNumber]);
  }

  return rows;
}

export function createTeacherSummary(
  exam: any,
  submissions: any[],
): {
  activeTaskDefinitions: TaskDefinition[];
  studentSummaries: TeacherStudentSummary[];
  totalPossiblePoints: number;
  averageGrade: number | null;
  averagePercentage: number;
  averageTotalPoints: number;
  gradeDistribution: Array<{ grade: string; count: number; ratio: number }>;
  languageUseGroupColumns: LanguageUseGroupColumn[];
  hasFiApplied: boolean;
} {
  const gradingScale = parseGradingScale(exam?.gradingScaleJson ?? null);
  const examTasks = Array.isArray(exam?.tasks) ? exam.tasks : [];
  const languageUseSchemeRules = Array.isArray(exam?.languageUseSchemeRules) ? exam.languageUseSchemeRules : [];

  const taskDefinitions: TaskDefinition[] = examTasks.map((task: any, index: number) => {
    const taskNumber = String(task?.taskNumber ?? index + 1);
    const maxPoints = computeAggregate(task);
    const labelText = taskNumber.toLowerCase().includes('aufgabe') ? taskNumber : `Aufgabe ${taskNumber}`;
    return {
      taskKey: taskNumber,
      label: labelText,
      maxPoints,
      languageUseMaxPoints: taskSoloLanguageUseMaxPoints(task, languageUseSchemeRules),
      hasOwnLanguageUseCriteria: taskHasSoloLanguageUseCriteria(task, languageUseSchemeRules),
    };
  });

  const fallbackTaskDefinitions: TaskDefinition[] =
    taskDefinitions.length > 0
      ? taskDefinitions
      : Array.from(
          new Set(
            submissions.flatMap(submission =>
              (Array.isArray(submission?.taskSubmissions) ? submission.taskSubmissions : []).map(
                (task: any, taskIndex: number) => String(task?.taskNumber ?? taskIndex + 1),
              ),
            ),
          ),
        ).map(taskNumber => ({
          taskKey: taskNumber,
          // No exam task to derive a max from (submissions-only fallback) → unknown max = 0, not a silent 1.
          label: taskNumber.toLowerCase().includes('aufgabe') ? taskNumber : `Aufgabe ${taskNumber}`,
          maxPoints: 0,
          languageUseMaxPoints: 0,
          hasOwnLanguageUseCriteria: false,
        }));

  const activeTaskDefinitions = fallbackTaskDefinitions;
  const languageUseMaxPoints = sumLanguageUseMaxPoints(exam);
  const totalPossiblePoints =
    activeTaskDefinitions.reduce((sum, task) => sum + task.maxPoints, 0) + languageUseMaxPoints;
  const languageUseGroupColumns = buildLanguageUseGroupColumns(exam);
  // "FI applied" is read straight off the persisted Submission.grammarGradingSummary (written by
  // persistGrammarFiOutcomes.ts whenever the teacher's FI switch is on) rather than re-derived from
  // task types/rules here — that's the only place that actually knows the switch state.
  const hasFiApplied = submissions.some(s => unwrapDoc<any>(s?.grammarGradingSummary)?.fiApplied === true);

  const studentSummaries: TeacherStudentSummary[] = submissions.map((submission, submissionIndex) => {
    const pointsByTask: Record<string, number> = {};
    const confidenceByTask: Record<string, number | null> = {};
    const isApprovedByTask: Record<string, boolean> = {};
    const hasManualApprovalByTask: Record<string, boolean> = {};
    const ownLanguageUsePointsByTask: Record<string, number> = {};

    for (const taskDef of activeTaskDefinitions) {
      const submissionTasks = Array.isArray(submission?.taskSubmissions) ? submission.taskSubmissions : [];
      const mainTaskByNumber = submissionTasks.find((task: any) => String(task?.taskNumber ?? '') === taskDef.taskKey);
      const mainTaskByIndex = submissionTasks[Number(taskDef.taskKey) - 1];
      const mainTask = mainTaskByNumber ?? mainTaskByIndex;

      if (!mainTask) {
        pointsByTask[taskDef.taskKey] = 0;
        confidenceByTask[taskDef.taskKey] = null;
        isApprovedByTask[taskDef.taskKey] = false;
        hasManualApprovalByTask[taskDef.taskKey] = false;
        ownLanguageUsePointsByTask[taskDef.taskKey] = 0;
        continue;
      }

      const relatedExamTask =
        examTasks.find((task: any) => String(task?.taskNumber ?? '') === taskDef.taskKey) ??
        examTasks[Number(taskDef.taskKey) - 1];

      ownLanguageUsePointsByTask[taskDef.taskKey] = Math.max(
        0,
        Math.min(
          taskSoloLanguageUseEarnedPoints(
            relatedExamTask,
            mainTask,
            languageUseSchemeRules,
            submission?.languageUseRuleResults,
            [taskDef.taskKey],
          ),
          taskDef.languageUseMaxPoints,
        ),
      );

      const resolveExamChildForSubmissionChild = (examParent: any, submissionChild: any, childIndex: number): any => {
        const examChildren = Array.isArray(examParent?.subTasks) ? examParent.subTasks : [];
        const byTaskNumber = examChildren.find(
          (examChild: any) => String(examChild?.taskNumber ?? '') === String(submissionChild?.taskNumber ?? ''),
        );
        return byTaskNumber ?? examChildren[childIndex];
      };

      const aggregateTaskTree = (
        submissionNode: any,
        examNode: any,
      ): {
        points: number;
        unresolvedConfidence: number | null;
        hasOpenClarification: boolean;
        hasManualApproval: boolean;
      } => {
        if (!submissionNode) {
          return {
            points: 0,
            unresolvedConfidence: null,
            hasOpenClarification: false,
            hasManualApproval: false,
          };
        }

        const resolvedSubmissionNode = unwrapDoc<any>(submissionNode) ?? submissionNode;
        const resolvedExamNode = unwrapDoc<any>(examNode) ?? examNode;
        const nodeNeedsAnswer = resolvedExamNode?.needsAnswer !== false;

        const nodeMaxPoints = ownMaxPoints(resolvedExamNode);
        const nodeGradingResult = unwrapDoc<any>(resolvedSubmissionNode?.gradingResult);
        // Only answerable nodes carry a grading result, so awarded is 0 for containers anyway; clamp own to
        // its max here (the subtree total is clamped once more at the task-def level below).
        const nodePoints = nodeNeedsAnswer
          ? Math.max(0, Math.min(awardedPoints(resolvedSubmissionNode), nodeMaxPoints))
          : 0;
        const nodeConfidence = nodeNeedsAnswer ? parseConfidenceRatio(nodeGradingResult?.confidence) : null;
        const nodeApproved = nodeNeedsAnswer ? nodeGradingResult?.isApproved === true : true;
        const nodeRequiresClarification =
          nodeConfidence != null && nodeConfidence < GRADING_THRESHOLDS.MEDIUM_CONFIDENCE && !nodeApproved;

        const submissionChildren = Array.isArray(resolvedSubmissionNode?.subTasksSubmission)
          ? resolvedSubmissionNode.subTasksSubmission
          : [];

        const childAggregates = submissionChildren.map((child: any, childIndex: number) => {
          const examChild = resolveExamChildForSubmissionChild(resolvedExamNode, child, childIndex);
          return aggregateTaskTree(child, examChild);
        });

        const childPoints = childAggregates.reduce((sum, child) => sum + child.points, 0);
        const childUnresolvedConfidences = childAggregates
          .map(child => child.unresolvedConfidence)
          .filter((value): value is number => value != null && Number.isFinite(value));

        const unresolvedConfidenceValues = [
          nodeRequiresClarification ? nodeConfidence : null,
          ...childUnresolvedConfidences,
        ].filter((value): value is number => value != null && Number.isFinite(value));

        const minUnresolvedConfidence =
          unresolvedConfidenceValues.length > 0 ? Math.min(...unresolvedConfidenceValues) : null;
        const hasOpenClarification =
          nodeRequiresClarification || childAggregates.some(child => child.hasOpenClarification);
        const hasManualApproval = nodeApproved || childAggregates.some(child => child.hasManualApproval);

        return {
          points: nodePoints + childPoints,
          unresolvedConfidence: minUnresolvedConfidence,
          hasOpenClarification,
          hasManualApproval,
        };
      };

      const aggregated = aggregateTaskTree(mainTask, relatedExamTask);

      pointsByTask[taskDef.taskKey] = Math.max(0, Math.min(aggregated.points, taskDef.maxPoints));
      confidenceByTask[taskDef.taskKey] = aggregated.unresolvedConfidence;
      isApprovedByTask[taskDef.taskKey] = !aggregated.hasOpenClarification;
      hasManualApprovalByTask[taskDef.taskKey] = aggregated.hasManualApproval;
    }

    // Part C2: this submission's shared per-group language-use results, matched by languageUseSchemeId,
    // routed through the same resolveLanguageUseGroupPoints primitive the client summary canvas uses.
    const submissionGroupResults = Array.isArray(submission?.languageUseRuleResults)
      ? submission.languageUseRuleResults
      : [];
    const examLanguageUseSchemes = Array.isArray(exam?.languageUseSchemeRules) ? exam.languageUseSchemeRules : [];
    const languageUseGroupPointsBySchemeId: Record<string, number> = {};
    for (const col of languageUseGroupColumns) {
      const matchingResult = submissionGroupResults.find(
        (r: any) => String(r?.languageUseSchemeId ?? '') === col.schemeId,
      );
      const scheme = examLanguageUseSchemes.find((s: any) => String(s?._id ?? '') === col.schemeId);
      languageUseGroupPointsBySchemeId[col.schemeId] = scheme
        ? resolveLanguageUseGroupPoints(scheme, matchingResult).earnedPoints
        : 0;
    }

    // Fehlerindex grade-deduction stats — read straight off the persisted grammarGradingSummary
    // (written by persistGrammarFiOutcomes.ts only when the teacher's FI switch is actually on),
    // rather than re-derived from task types/rules here.
    const grammarSummary = unwrapDoc<any>(submission?.grammarGradingSummary);
    const fiApplied = grammarSummary?.fiApplied === true;
    const fiErrorCount = fiApplied ? parseNumber(grammarSummary.errorCount) ?? 0 : null;
    const fiWordCount = fiApplied ? parseNumber(grammarSummary.wordCount) ?? 0 : null;
    const fiFehlerindex = fiApplied ? parseNumber(grammarSummary.fehlerindex) ?? 0 : null;

    // Language-use rules (single-task merged criteria and multi-task LanguageUseScheme groups) count
    // toward the grade but aren't part of the content-only pointsByTask breakdown — added to the grand total.
    const languageUseEarnedPoints = sumLanguageUseEarnedPoints(exam, submission);
    const hasOpenLanguageUseClarification = hasSubmissionOpenLanguageUseClarification(exam, submission);
    const totalPoints =
      activeTaskDefinitions.reduce((sum, taskDef) => sum + (pointsByTask[taskDef.taskKey] ?? 0), 0) +
      languageUseEarnedPoints;
    const totalMaxPoints =
      activeTaskDefinitions.reduce((sum, taskDef) => sum + taskDef.maxPoints, 0) + languageUseMaxPoints;
    const percentage = totalMaxPoints > 0 ? (totalPoints / totalMaxPoints) * 100 : 0;
    const baseGrade = gradeFromPercentage(percentage, gradingScale);
    const fiDeductionSteps = resolveFiDeductionSteps(submission);
    const grade = applyGradeStepDeduction(baseGrade, fiDeductionSteps, gradingScale);

    return {
      id: String(submission?._id ?? submissionIndex + 1),
      submissionIndex,
      name:
        normalizeText(submission?.studentName) ||
        normalizeText(submission?.studentId) ||
        `Schüler ${submissionIndex + 1}`,
      pointsByTask,
      confidenceByTask,
      isApprovedByTask,
      hasManualApprovalByTask,
      hasOpenLanguageUseClarification,
      ownLanguageUsePointsByTask,
      languageUseGroupPointsBySchemeId,
      fiApplied,
      fiErrorCount,
      fiWordCount,
      fiFehlerindex,
      totalPoints,
      totalMaxPoints,
      percentage,
      grade,
      baseGrade,
      fiDeductionSteps,
    };
  });

  const averageGrade =
    studentSummaries.length > 0
      ? (() => {
          const avg = averageNumericGrade(
            studentSummaries.map(s => s.grade),
            gradingScale,
          );
          return avg != null ? roundToOne(avg) : null;
        })()
      : null;

  const averagePercentage =
    studentSummaries.length > 0
      ? roundToOne(studentSummaries.reduce((sum, student) => sum + student.percentage, 0) / studentSummaries.length)
      : 0;

  const averageTotalPoints =
    studentSummaries.length > 0
      ? roundToOne(studentSummaries.reduce((sum, student) => sum + student.totalPoints, 0) / studentSummaries.length)
      : 0;

  const gradeDistribution = gradingScale.borders.map(border => {
    const count = studentSummaries.filter(student => student.grade === border.label).length;
    const ratio = studentSummaries.length > 0 ? (count / studentSummaries.length) * 100 : 0;
    return { grade: border.label, count, ratio };
  });

  return {
    activeTaskDefinitions,
    studentSummaries,
    totalPossiblePoints,
    averageGrade,
    averagePercentage,
    averageTotalPoints,
    gradeDistribution,
    languageUseGroupColumns,
    hasFiApplied,
  };
}

export async function buildTeacherSummaryPDFBytes(options: { exam: any; submissions: any[] }) {
  const { exam, submissions } = options;
  const summary = createTeacherSummary(exam, submissions);

  const doc = await PDFDocument.create();
  const PW = 595,
    PH = 842,
    M = 40,
    CW = PW - M * 2;
  const fontR = await doc.embedFont(StandardFonts.Helvetica);
  const fontB = await doc.embedFont(StandardFonts.HelveticaBold);
  const logoImg = await doc.embedPng(OWL_PNG_BASE64);

  // Frontend brand colors (#5594CF brand-500 palette)
  const brand = rgb(0.333, 0.58, 0.812); // #5594CF
  const brandMid = rgb(0.6, 0.749, 0.886); // #99BFE2
  const brandBg = rgb(0.941, 0.969, 1.0); // ~#F0F7FF brand-50
  const white = rgb(1, 1, 1);
  const textDark = rgb(0.059, 0.09, 0.165); // #0f172a slate-900
  const textGray = rgb(0.396, 0.455, 0.549); // slate-500
  const rowAlt = rgb(0.953, 0.961, 0.97); // slate-100
  const border = rgb(0.882, 0.906, 0.929); // slate-200

  let page = doc.addPage([PW, PH]);
  let y = PH;
  let newPageFlag = false;

  const nextPage = () => {
    page = doc.addPage([PW, PH]);
    y = PH - M;
    newPageFlag = true;
  };
  const need = (h: number) => {
    if (y - h < M + 30) nextPage();
  };

  const examTitle = normalizeText(exam?.title) || 'Klausur';
  const HH = 70; // header height

  // Header band
  page.drawRectangle({ x: 0, y: PH - HH, width: PW, height: HH, color: brand });
  // Owl badge (white box + image)
  page.drawRectangle({ x: M, y: PH - HH + 13, width: 44, height: 44, color: white });
  page.drawImage(logoImg, { x: M + 2, y: PH - HH + 15, width: 40, height: 40 });
  // "gradias" brand text
  page.drawText('gradias', { x: M + 52, y: PH - HH + 35, size: 16, font: fontB, color: white });
  // Vertical divider
  page.drawLine({
    start: { x: M + 132, y: PH - 12 },
    end: { x: M + 132, y: PH - HH + 10 },
    thickness: 0.5,
    color: rgb(1, 1, 1),
  });
  // Report title + exam name
  page.drawText('Lehrer-Zusammenfassung', { x: M + 142, y: PH - 26, size: 14, font: fontB, color: white });
  page.drawText(toPdfSafeText(examTitle.slice(0, 60)), {
    x: M + 142,
    y: PH - 45,
    size: 9,
    font: fontR,
    color: brandMid,
  });
  y = PH - HH;

  // Info row
  page.drawRectangle({ x: 0, y: y - 24, width: PW, height: 24, color: rowAlt });
  page.drawText(toPdfSafeText(`Generiert am: ${new Date().toLocaleString('de-DE')}`), {
    x: M,
    y: y - 16,
    size: 8.5,
    font: fontR,
    color: textGray,
  });
  y -= 36;

  // KPI boxes
  const bH = 52,
    bGap = 10,
    bW = (CW - bGap * 2) / 3;
  const avgGradeStr =
    summary.averageGrade != null ? summary.averageGrade.toFixed(1) : `${summary.averagePercentage.toFixed(1)}%`;
  const avgPtsStr = `${summary.averageTotalPoints.toFixed(1)} / ${roundToOne(summary.totalPossiblePoints)} P.`;
  [
    { label: 'Anzahl Schüler', value: `${summary.studentSummaries.length}` },
    { label: 'Notenschnitt', value: avgGradeStr },
    { label: 'Ø Punkte', value: avgPtsStr },
  ].forEach((k, i) => {
    const bx = M + i * (bW + bGap),
      by = y - bH;
    page.drawRectangle({ x: bx, y: by, width: bW, height: bH, color: brandBg });
    page.drawRectangle({ x: bx, y: by + bH - 3, width: bW, height: 3, color: brandMid });
    const lw = fontR.widthOfTextAtSize(k.label, 8);
    page.drawText(toPdfSafeText(k.label), {
      x: bx + (bW - lw) / 2,
      y: by + bH - 14,
      size: 8,
      font: fontR,
      color: textGray,
    });
    const vSize = k.value.length > 10 ? 12 : k.value.length > 6 ? 16 : 22;
    const vw = fontB.widthOfTextAtSize(toPdfSafeText(k.value), vSize);
    page.drawText(toPdfSafeText(k.value), {
      x: bx + (bW - vw) / 2,
      y: by + 10,
      size: vSize,
      font: fontB,
      color: brand,
    });
  });
  y -= bH + 14;

  page.drawLine({ start: { x: M, y }, end: { x: PW - M, y }, thickness: 0.5, color: border });
  y -= 14;

  // Student table — columns: Name | content tasks + own-criteria language-use rules + language-use
  // groups + Fehlerindex | Gesamt | % | Note. Column count is capped so no column renders narrower than
  // MIN_COL_W; if the exam has more language-use-aware columns than fit, trailing ones are dropped and
  // a note is drawn beneath the table (full detail is always in the CSV export).
  const allColumns = buildLanguageUseAwareColumns(summary);
  const nameW = 100,
    totalW = 48,
    pctW = 36,
    noteW = 32,
    // "Note mit FI" only exists as a second grade column once FI is actually applied for this exam —
    // when it's not, the single "Note" column keeps showing the (unchanged) final grade, exactly as
    // before this feature.
    noteFiW = summary.hasFiApplied ? 40 : 0;
  const fixedW = nameW + totalW + pctW + noteW + noteFiW;
  const MIN_COL_W = 28;
  const maxVisibleColumns = Math.max(0, Math.floor((CW - fixedW) / MIN_COL_W));
  const visibleColumns = allColumns.slice(0, maxVisibleColumns);
  const droppedColumnCount = allColumns.length - visibleColumns.length;
  const colW =
    visibleColumns.length > 0 ? Math.max(MIN_COL_W, Math.floor((CW - fixedW) / visibleColumns.length)) : 0;
  const tableW = nameW + visibleColumns.length * colW + totalW + pctW + noteW + noteFiW;

  const shortColumnHeader = (c: LanguageUseAwareColumn): string => {
    switch (c.kind) {
      case 'task':
        return c.label.replace('Aufgabe ', 'A');
      case 'ownLanguageUse':
        return `A${c.taskKey}·SL`;
      case 'group':
        return `Gr:${c.dependsOn.map(formatTaskReferenceForDisplay).join(',')}`;
      case 'fehlerindex':
        return 'FI';
    }
  };

  const columnCellText = (c: LanguageUseAwareColumn, student: TeacherStudentSummary): string => {
    if (c.kind === 'fehlerindex') {
      return student.fiFehlerindex == null ? '-' : roundToOne(student.fiFehlerindex).toFixed(1);
    }
    return `${roundToOne(languageUseColumnEarnedPoints(c, student))}/${roundToOne(c.maxPoints)}`;
  };

  const drawTableHeader = () => {
    need(20);
    page.drawRectangle({ x: M, y: y - 20, width: tableW, height: 20, color: brand });
    let cx = M;
    for (const col of [
      { label: 'Name', w: nameW },
      ...visibleColumns.map(c => ({ label: toPdfSafeText(shortColumnHeader(c)), w: colW })),
      { label: 'Gesamt', w: totalW },
      { label: '%', w: pctW },
      { label: 'Note', w: noteW },
      ...(summary.hasFiApplied ? [{ label: 'Note (FI)', w: noteFiW }] : []),
    ]) {
      const lw = fontB.widthOfTextAtSize(col.label, 7.5);
      page.drawText(col.label, { x: cx + (col.w - lw) / 2, y: y - 14, size: 7.5, font: fontB, color: white });
      cx += col.w;
    }
    y -= 20;
  };

  page.drawText('Schülerübersicht', { x: M, y, size: 12, font: fontB, color: brand });
  y -= 16;
  drawTableHeader();

  for (let i = 0; i < summary.studentSummaries.length; i++) {
    const student = summary.studentSummaries[i]!;
    newPageFlag = false;
    need(17);
    if (newPageFlag) drawTableHeader();

    page.drawRectangle({ x: M, y: y - 17, width: tableW, height: 17, color: i % 2 === 1 ? rowAlt : white });
    let cx = M;

    const nameText = toPdfSafeText(student.name.length > 17 ? `${student.name.slice(0, 16)}.` : student.name);
    page.drawText(nameText, { x: cx + 4, y: y - 12, size: 8.5, font: fontR, color: textDark });
    cx += nameW;

    for (const col of visibleColumns) {
      const txt = toPdfSafeText(columnCellText(col, student));
      const tw = fontR.widthOfTextAtSize(txt, 7.5);
      page.drawText(txt, { x: cx + (colW - tw) / 2, y: y - 12, size: 7.5, font: fontR, color: textDark });
      cx += colW;
    }

    const totalTxt = toPdfSafeText(`${roundToOne(student.totalPoints)}/${roundToOne(student.totalMaxPoints)}`);
    const totalTW = fontB.widthOfTextAtSize(totalTxt, 8);
    page.drawText(totalTxt, { x: cx + (totalW - totalTW) / 2, y: y - 12, size: 8, font: fontB, color: textDark });
    cx += totalW;

    const pctTxt = toPdfSafeText(`${roundToOne(student.percentage)}%`);
    const pctTW = fontR.widthOfTextAtSize(pctTxt, 8);
    page.drawText(pctTxt, { x: cx + (pctW - pctTW) / 2, y: y - 12, size: 8, font: fontR, color: textGray });
    cx += pctW;

    // "Note" shows the base (pre-FI) grade once "Note (FI)" exists as a separate column for the
    // final, deduction-adjusted grade; otherwise it stays the single final grade, as before.
    const gradeTxt = toPdfSafeText(summary.hasFiApplied ? student.baseGrade : student.grade);
    const gradeTW = fontB.widthOfTextAtSize(gradeTxt, 8.5);
    page.drawText(gradeTxt, { x: cx + (noteW - gradeTW) / 2, y: y - 12, size: 8.5, font: fontB, color: brand });
    cx += noteW;

    if (summary.hasFiApplied) {
      const gradeFiTxt = toPdfSafeText(student.grade);
      const gradeFiTW = fontB.widthOfTextAtSize(gradeFiTxt, 8.5);
      page.drawText(gradeFiTxt, { x: cx + (noteFiW - gradeFiTW) / 2, y: y - 12, size: 8.5, font: fontB, color: brand });
    }

    y -= 17;
  }

  page.drawLine({ start: { x: M, y }, end: { x: M + tableW, y }, thickness: 0.5, color: border });
  y -= 14;

  if (droppedColumnCount > 0) {
    need(14);
    page.drawText(
      toPdfSafeText(
        `Hinweis: ${droppedColumnCount} weitere Spalte(n) zur sprachlichen Leistung aus Platzgründen nicht dargestellt — vollständige Übersicht siehe CSV-Export.`,
      ),
      { x: M, y, size: 7.5, font: fontR, color: textGray },
    );
    y -= 14;
  }
  y -= 4;

  // Grade distribution
  const nonEmptyGrades = summary.gradeDistribution.filter(g => g.count > 0);
  if (nonEmptyGrades.length > 0) {
    need(30 + nonEmptyGrades.length * 18);
    page.drawLine({ start: { x: M, y }, end: { x: PW - M, y }, thickness: 0.5, color: border });
    y -= 14;
    page.drawText('Notenverteilung', { x: M, y, size: 12, font: fontB, color: brand });
    y -= 18;

    const maxCount = Math.max(...nonEmptyGrades.map(g => g.count), 1);
    const barAreaW = CW - 105;

    for (const item of nonEmptyGrades) {
      need(18);
      page.drawText(toPdfSafeText(`Note ${item.grade}`), { x: M, y: y - 2, size: 9, font: fontB, color: textDark });
      const barW = Math.max(4, Math.round((item.count / maxCount) * barAreaW));
      page.drawRectangle({ x: M + 55, y: y - 12, width: barW, height: 12, color: brandBg });
      page.drawRectangle({ x: M + 55, y: y - 12, width: 3, height: 12, color: brand });
      page.drawText(toPdfSafeText(`${item.count} (${roundToOne(item.ratio)}%)`), {
        x: M + 55 + barW + 6,
        y: y - 2,
        size: 8.5,
        font: fontR,
        color: textGray,
      });
      y -= 16;
    }
  }

  // Footers
  const pageCount = doc.getPageCount();
  doc.getPages().forEach((p, i) => {
    const ft = `Gradias  |  Seite ${i + 1} von ${pageCount}`;
    const ftW = fontR.widthOfTextAtSize(ft, 8);
    p.drawLine({ start: { x: M, y: 28 }, end: { x: PW - M, y: 28 }, thickness: 0.3, color: border });
    p.drawText(toPdfSafeText(ft), { x: (PW - ftW) / 2, y: 16, size: 8, font: fontR, color: textGray });
  });

  return doc.save();
}

export async function buildSubmissionReportPDFBytes(options: { exam: any; submission: any; submissionIndex: number }) {
  const { exam, submission, submissionIndex } = options;
  const taskRows = createSubmissionTaskRows(exam, submission);

  // Language-use rules (own-criteria and multi-task LanguageUseScheme groups) count toward the grade
  // but aren't part of the content-only taskRows breakdown — added to the grand total, same as the
  // teacher summary (createTeacherSummary) already does.
  const contentPoints = taskRows.reduce((sum, row) => sum + row.points, 0);
  const contentMaxPoints = taskRows.reduce((sum, row) => sum + row.maxPoints, 0);
  const totalPoints = contentPoints + sumLanguageUseEarnedPoints(exam, submission);
  const totalMaxPoints = contentMaxPoints + sumLanguageUseMaxPoints(exam);
  const percentage = totalMaxPoints > 0 ? (totalPoints / totalMaxPoints) * 100 : 0;
  const scale = parseGradingScale(exam?.gradingScaleJson ?? null);
  const baseGrade = gradeFromPercentage(percentage, scale);
  const fiSteps = resolveFiDeductionSteps(submission);
  const grade = applyGradeStepDeduction(baseGrade, fiSteps, scale);

  const doc = await PDFDocument.create();
  const PW = 595,
    PH = 842,
    M = 40,
    CW = PW - M * 2;
  const fontR = await doc.embedFont(StandardFonts.Helvetica);
  const fontB = await doc.embedFont(StandardFonts.HelveticaBold);
  const fontMono = await doc.embedFont(StandardFonts.Courier);
  const logoImg = await doc.embedPng(OWL_PNG_BASE64);

  // Frontend brand colors (#5594CF brand-500 palette)
  const brand = rgb(0.333, 0.58, 0.812); // #5594CF
  const brandMid = rgb(0.6, 0.749, 0.886); // #99BFE2
  const brandBg = rgb(0.941, 0.969, 1.0); // ~#F0F7FF brand-50
  const white = rgb(1, 1, 1);
  const textDark = rgb(0.059, 0.09, 0.165); // #0f172a slate-900
  const textGray = rgb(0.396, 0.455, 0.549); // slate-500
  const rowAlt = rgb(0.953, 0.961, 0.97); // slate-100
  const border = rgb(0.882, 0.906, 0.929); // slate-200
  const green = rgb(0.063, 0.725, 0.506); // #10b981 emerald-500
  const red = rgb(0.937, 0.267, 0.267); // #ef4444 red-500

  let page = doc.addPage([PW, PH]);
  let y = PH;

  const nextPage = () => {
    page = doc.addPage([PW, PH]);
    y = PH - M;
  };
  const need = (h: number) => {
    if (y - h < M + 30) nextPage();
  };

  // Renders Markdown + LaTeX content into the PDF at the current y position.
  // Closes over page/y/need/fonts/colors — mutations to y are visible in the outer scope.
  const drawRichContent = (content: string, x: number, maxW: number, baseSize = 9) => {
    if (!content) return;
    const richLines = parseMarkdownLines(latexToSafeText(content));
    for (const rl of richLines) {
      const isHead = rl.style === 'h1' || rl.style === 'h2' || rl.style === 'h3';
      const font = rl.style === 'code' ? fontMono : isHead ? fontB : fontR;
      const fSize =
        rl.style === 'h1'
          ? baseSize + 3
          : rl.style === 'h2'
            ? baseSize + 2
            : rl.style === 'h3'
              ? baseSize + 1
              : baseSize;
      const indent = rl.style === 'bullet' ? 10 : rl.style === 'blockquote' ? 14 : 0;
      const prefix = rl.style === 'bullet' ? '- ' : '';
      const color = isHead ? brand : rl.style === 'blockquote' ? textGray : textDark;

      const raw = toPdfSafeText(prefix + rl.text);
      if (!raw && rl.style === 'body') {
        y -= 4;
        continue;
      }

      const wrapped = raw ? wrapText(raw, font, fSize, maxW - indent) : [''];
      const blockH = wrapped.length * (fSize + 3) + (rl.style === 'code' ? 4 : 0);
      need(blockH + 2);

      if (rl.style === 'code') {
        page.drawRectangle({
          x: x + indent - 2,
          y: y - blockH + 2,
          width: maxW - indent + 2,
          height: blockH - 2,
          color: rowAlt,
        });
      }
      if (rl.style === 'blockquote') {
        page.drawRectangle({ x: x + indent - 4, y: y - blockH + 2, width: 2, height: blockH - 2, color: brandMid });
      }
      for (const wl of wrapped) {
        need(fSize + 3);
        if (wl) page.drawText(wl, { x: x + indent, y, size: fSize, font, color });
        y -= fSize + 3;
      }
      if (isHead) y -= 2;
    }
  };

  const studentLabel =
    normalizeText(submission?.studentName) || normalizeText(submission?.studentId) || `Schüler ${submissionIndex + 1}`;
  const examTitle = normalizeText(exam?.title) || 'Klausur';
  const HH = 70; // header height

  // Header band
  page.drawRectangle({ x: 0, y: PH - HH, width: PW, height: HH, color: brand });
  // Owl badge (white box + image)
  page.drawRectangle({ x: M, y: PH - HH + 13, width: 44, height: 44, color: white });
  page.drawImage(logoImg, { x: M + 2, y: PH - HH + 15, width: 40, height: 40 });
  // "gradias" brand text
  page.drawText('gradias', { x: M + 52, y: PH - HH + 35, size: 16, font: fontB, color: white });
  // Vertical divider
  page.drawLine({
    start: { x: M + 132, y: PH - 12 },
    end: { x: M + 132, y: PH - HH + 10 },
    thickness: 0.5,
    color: rgb(1, 1, 1),
  });
  // Report title + subtitle
  page.drawText('Korrekturbericht', { x: M + 142, y: PH - 26, size: 14, font: fontB, color: white });
  const rawSub = `${examTitle}  |  ${studentLabel}`;
  page.drawText(toPdfSafeText(rawSub.length > 60 ? `${rawSub.slice(0, 57)}...` : rawSub), {
    x: M + 142,
    y: PH - 45,
    size: 9,
    font: fontR,
    color: brandMid,
  });
  y = PH - HH;

  // Info row
  page.drawRectangle({ x: 0, y: y - 24, width: PW, height: 24, color: rowAlt });
  page.drawText(toPdfSafeText(`Generiert am: ${new Date().toLocaleString('de-DE')}`), {
    x: M,
    y: y - 16,
    size: 8.5,
    font: fontR,
    color: textGray,
  });
  y -= 36;

  // Result boxes
  const bH = 56,
    bGap = 10,
    bW = (CW - bGap * 2) / 3;
  [
    { label: 'Erreichte Punkte', value: `${totalPoints.toFixed(1)} / ${totalMaxPoints.toFixed(1)}` },
    { label: 'Prozent', value: `${percentage.toFixed(1)}%` },
    { label: fiSteps > 0 ? 'Note (inkl. FI-Abzug)' : 'Note', value: grade },
  ].forEach((b, i) => {
    const bx = M + i * (bW + bGap),
      by = y - bH;
    page.drawRectangle({ x: bx, y: by, width: bW, height: bH, color: brandBg });
    page.drawRectangle({ x: bx, y: by + bH - 3, width: bW, height: 3, color: brandMid });
    const lw = fontR.widthOfTextAtSize(b.label, 8);
    page.drawText(toPdfSafeText(b.label), {
      x: bx + (bW - lw) / 2,
      y: by + bH - 14,
      size: 8,
      font: fontR,
      color: textGray,
    });
    const vSize = b.value.length > 11 ? 13 : b.value.length > 7 ? 17 : 22;
    const vw = fontB.widthOfTextAtSize(toPdfSafeText(b.value), vSize);
    page.drawText(toPdfSafeText(b.value), {
      x: bx + (bW - vw) / 2,
      y: by + 10,
      size: vSize,
      font: fontB,
      color: brand,
    });
  });
  y -= bH + 12;

  page.drawLine({ start: { x: M, y }, end: { x: PW - M, y }, thickness: 0.5, color: border });
  y -= 14;

  // Pre-embed task images so dimensions are known before drawing.
  const maxImgW = CW - 16;
  const maxImgH = 150;
  const embeddedImagesForRow = new Map<number, Array<{ img: PDFImage; w: number; h: number }>>();
  for (let ri = 0; ri < taskRows.length; ri++) {
    const row = taskRows[ri]!;
    if (!row.imageIds.length) continue;
    const rawImgs = await fetchTaskImages(row.imageIds);
    const embedded: Array<{ img: PDFImage; w: number; h: number }> = [];
    for (const { bytes, isPng } of rawImgs) {
      try {
        const img = isPng ? await doc.embedPng(bytes) : await doc.embedJpg(bytes);
        const scale = Math.min(maxImgW / img.width, maxImgH / img.height, 1);
        embedded.push({ img, w: Math.round(img.width * scale), h: Math.round(img.height * scale) });
      } catch {
        /* skip unembeddable image */
      }
    }
    if (embedded.length) embeddedImagesForRow.set(ri, embedded);
  }

  // Tasks
  for (let ri = 0; ri < taskRows.length; ri++) {
    const row = taskRows[ri]!;
    need(80);

    // Task header bar with left accent
    page.drawRectangle({ x: M, y: y - 22, width: CW, height: 22, color: brandBg });
    page.drawRectangle({ x: M, y: y - 22, width: 4, height: 22, color: brand });
    page.drawText(toPdfSafeText(row.label), { x: M + 10, y: y - 15, size: 10.5, font: fontB, color: textDark });
    const ptTxt = toPdfSafeText(`${row.points.toFixed(1)} / ${row.maxPoints.toFixed(1)} P.`);
    const ptW = fontB.widthOfTextAtSize(ptTxt, 10);
    const ptColor = row.points >= row.maxPoints ? green : row.points === 0 ? red : brand;
    page.drawText(ptTxt, { x: M + CW - ptW - 8, y: y - 15, size: 10, font: fontB, color: ptColor });
    y -= 28;

    // Scanned answer images
    const images = embeddedImagesForRow.get(ri);
    if (images && images.length > 0) {
      page.drawText('Scan:', { x: M + 8, y, size: 8.5, font: fontB, color: brand });
      y -= 10;
      for (const { img, w, h } of images) {
        need(h + 8);
        const ix = M + 8 + Math.max(0, (maxImgW - w) / 2);
        page.drawImage(img, { x: ix, y: y - h, width: w, height: h });
        y -= h + 6;
      }
      y -= 4;
      page.drawLine({ start: { x: M + 8, y }, end: { x: PW - M - 8, y }, thickness: 0.3, color: border });
      y -= 8;
    }

    // Content sections with Markdown + LaTeX rendering
    for (const [title, content] of [
      ['Aufgabe', row.description],
      ['Schülerantwort (extrahiert)', row.studentAnswer],
      ['Bewertungsschema', row.gradingScheme],
      ['Musterlösung', row.masterSolution],
      ['Begründung', row.explanation],
    ] as Array<[string, string]>) {
      if (!content) continue;
      need(26);
      page.drawText(toPdfSafeText(`${title}:`), { x: M + 8, y, size: 8.5, font: fontB, color: brand });
      y -= 13;
      drawRichContent(content, M + 16, CW - 32);
      y -= 4;
    }

    y -= 2;
    page.drawLine({ start: { x: M, y }, end: { x: PW - M, y }, thickness: 0.3, color: border });
    y -= 12;

    // Own-criteria language-use rule (case 2): rendered as its own pseudo-task section — same
    // header-bar style as the content task above, but no master solution/scan, plus the detected
    // mistake list.
    if (row.ownLanguageUse) {
      need(50);
      page.drawRectangle({ x: M, y: y - 22, width: CW, height: 22, color: brandBg });
      page.drawRectangle({ x: M, y: y - 22, width: 4, height: 22, color: brand });
      page.drawText(toPdfSafeText(`${row.label} · Sprachliche Leistung`), {
        x: M + 10,
        y: y - 15,
        size: 10.5,
        font: fontB,
        color: textDark,
      });
      const gPtTxt = toPdfSafeText(
        `${row.ownLanguageUse.points.toFixed(1)} / ${row.ownLanguageUse.maxPoints.toFixed(1)} P.`,
      );
      const gPtW = fontB.widthOfTextAtSize(gPtTxt, 10);
      const gPtColor =
        row.ownLanguageUse.points >= row.ownLanguageUse.maxPoints ? green : row.ownLanguageUse.points === 0 ? red : brand;
      page.drawText(gPtTxt, { x: M + CW - gPtW - 8, y: y - 15, size: 10, font: fontB, color: gPtColor });
      y -= 28;

      for (const [title, content] of [
        ['Bewertungsschema', row.ownLanguageUse.criteria],
        ['Begründung', row.ownLanguageUse.explanation],
      ] as Array<[string, string]>) {
        if (!content) continue;
        need(26);
        page.drawText(toPdfSafeText(`${title}:`), { x: M + 8, y, size: 8.5, font: fontB, color: brand });
        y -= 13;
        drawRichContent(content, M + 16, CW - 32);
        y -= 4;
      }

      y -= 2;
      page.drawLine({ start: { x: M, y }, end: { x: PW - M, y }, thickness: 0.3, color: border });
      y -= 12;
    }
  }

  // Language-use group results (case 3): multi-task LanguageUseScheme groups, shared across the tasks
  // they depend on — don't belong to a single task section, so rendered as their own section.
  const languageUseSchemeRules = Array.isArray(exam?.languageUseSchemeRules) ? exam.languageUseSchemeRules : [];
  const groupResults = Array.isArray(submission?.languageUseRuleResults) ? submission.languageUseRuleResults : [];
  const groupRows = languageUseSchemeRules.map((scheme: any) => {
    const result = groupResults.find((r: any) => String(r?.languageUseSchemeId ?? '') === String(scheme?._id ?? ''));
    const { maxPoints, earnedPoints } = resolveLanguageUseGroupPoints(scheme, result);
    const dependsOn = Array.isArray(scheme?.dependsOn) ? scheme.dependsOn : [];
    return {
      label: `Gruppe: Aufgabe ${dependsOn.map(formatTaskReferenceForDisplay).join(', ')}`,
      earnedPoints,
      maxPoints,
      explanation: preserveNewlines(result?.explanation),
    };
  });

  if (groupRows.length > 0) {
    need(40);
    page.drawText('Sprachliche Leistung (Gruppenbewertung)', { x: M, y, size: 12, font: fontB, color: brand });
    y -= 18;

    for (const g of groupRows) {
      need(30);
      page.drawRectangle({ x: M, y: y - 20, width: CW, height: 20, color: brandBg });
      page.drawRectangle({ x: M, y: y - 20, width: 4, height: 20, color: brand });
      page.drawText(toPdfSafeText(g.label), { x: M + 10, y: y - 14, size: 9.5, font: fontB, color: textDark });
      const gPtTxt = toPdfSafeText(`${g.earnedPoints.toFixed(1)} / ${g.maxPoints.toFixed(1)} P.`);
      const gPtW = fontB.widthOfTextAtSize(gPtTxt, 9);
      page.drawText(gPtTxt, { x: M + CW - gPtW - 8, y: y - 14, size: 9, font: fontB, color: brand });
      y -= 24;

      if (g.explanation) {
        drawRichContent(g.explanation, M + 16, CW - 32);
        y -= 4;
      }

      y -= 2;
      page.drawLine({ start: { x: M, y }, end: { x: PW - M, y }, thickness: 0.3, color: border });
      y -= 12;
    }
  }

  // Fehlerindex: one consolidated section for the whole submission, covering every grammar-eligible
  // task's detected mistakes (own-criteria, group, or otherwise — FI is an aggregate/exam-wide
  // concept, so breaking it up per task wouldn't make sense) plus the stats behind the grade
  // deduction. Only shown when FI is actually applied for this submission — sourced straight off the
  // persisted grammarGradingSummary (see resolveFiDeductionSteps above) rather than re-derived here.
  const grammarSummary = unwrapDoc<any>(submission?.grammarGradingSummary);
  if (grammarSummary?.fiApplied === true) {
    const wordCount = parseNumber(grammarSummary.wordCount) ?? 0;
    const errorCount = parseNumber(grammarSummary.errorCount) ?? 0;
    const fehlerindex = parseNumber(grammarSummary.fehlerindex) ?? 0;
    const deductionSteps = resolveFiDeductionSteps(submission);

    need(40);
    page.drawText('Fehlerindex', { x: M, y, size: 12, font: fontB, color: brand });
    y -= 18;

    const fiTiles = [
      { label: 'Wörter', value: `${wordCount}` },
      { label: 'Fehler', value: `${errorCount}` },
      { label: 'Fehlerindex', value: fehlerindex.toFixed(1) },
      { label: 'Notenabzug', value: deductionSteps > 0 ? `${deductionSteps} Stufe${deductionSteps > 1 ? 'n' : ''}` : 'keiner' },
    ];
    const fiGap = 8;
    const fiTileW = (CW - fiGap * (fiTiles.length - 1)) / fiTiles.length;
    const fiTileH = 48;
    need(fiTileH + 10);
    fiTiles.forEach((tile, i) => {
      const bx = M + i * (fiTileW + fiGap);
      const by = y - fiTileH;
      page.drawRectangle({ x: bx, y: by, width: fiTileW, height: fiTileH, color: brandBg });
      page.drawRectangle({ x: bx, y: by + fiTileH - 3, width: fiTileW, height: 3, color: brandMid });
      const lw = fontR.widthOfTextAtSize(tile.label, 8);
      page.drawText(toPdfSafeText(tile.label), {
        x: bx + (fiTileW - lw) / 2,
        y: by + fiTileH - 14,
        size: 8,
        font: fontR,
        color: textGray,
      });
      const vSize = tile.value.length > 9 ? 13 : 17;
      const vw = fontB.widthOfTextAtSize(toPdfSafeText(tile.value), vSize);
      page.drawText(toPdfSafeText(tile.value), {
        x: bx + (fiTileW - vw) / 2,
        y: by + 10,
        size: vSize,
        font: fontB,
        color: brand,
      });
    });
    y -= fiTileH + 14;

    const allErrors = taskRows.flatMap(row => row.grammarErrors.map(error => ({ ...error, taskLabel: row.label })));
    need(20);
    page.drawText('Erkannte Fehler:', { x: M + 8, y, size: 8.5, font: fontB, color: brand });
    y -= 13;
    if (allErrors.length > 0) {
      const errorsContent = allErrors
        .map(
          error =>
            `- ${error.taskLabel}: ${error.message}${error.category ? ` (${error.category})` : ''}${
              error.context ? `\n> "${error.context}"` : ''
            }`,
        )
        .join('\n');
      drawRichContent(errorsContent, M + 16, CW - 32);
    } else {
      need(14);
      page.drawText('Keine Fehler erkannt.', { x: M + 16, y, size: 9, font: fontR, color: textGray });
      y -= 13;
    }
  }

  // Footers
  const pageCount = doc.getPageCount();
  doc.getPages().forEach((p, i) => {
    const ft = `Gradias  |  Seite ${i + 1} von ${pageCount}`;
    const ftW = fontR.widthOfTextAtSize(ft, 8);
    p.drawLine({ start: { x: M, y: 28 }, end: { x: PW - M, y: 28 }, thickness: 0.3, color: border });
    p.drawText(toPdfSafeText(ft), { x: (PW - ftW) / 2, y: 16, size: 8, font: fontR, color: textGray });
  });

  return doc.save();
}

/** Earned points for a language-use-aware column ('fehlerindex' has no points concept — callers must
 * branch on `kind` before calling this for that case). */
function languageUseColumnEarnedPoints(column: LanguageUseAwareColumn, s: TeacherStudentSummary): number {
  switch (column.kind) {
    case 'task':
      return s.pointsByTask[column.taskKey] ?? 0;
    case 'ownLanguageUse':
      return s.ownLanguageUsePointsByTask[column.taskKey] ?? 0;
    case 'group':
      return s.languageUseGroupPointsBySchemeId[column.schemeId] ?? 0;
    case 'fehlerindex':
      return 0;
  }
}

export function buildTeacherSummaryCsv(options: { exam: any; submissions: any[] }): Buffer {
  const { exam, submissions } = options;
  const summary = createTeacherSummary(exam, submissions);
  const columns = buildLanguageUseAwareColumns(summary);
  const columnLabel = (c: LanguageUseAwareColumn): string => (c.kind === 'fehlerindex' ? 'Fehlerindex' : c.label);
  const columnMax = (c: LanguageUseAwareColumn): string =>
    c.kind === 'fehlerindex' ? '' : String(roundToOne(c.maxPoints));

  const esc = (v: string | number): string => {
    const s = String(v);
    return s.includes(';') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const rows: string[][] = [];
  // "Note mit FI" only exists once FI is actually applied for this exam — "Note" then shows the
  // base (pre-FI) grade instead of the final one, since that's now shown in the new column.
  const gradeColumns = summary.hasFiApplied ? ['Note', 'Note mit FI'] : ['Note'];

  // Header
  rows.push(['Name', ...columns.map(columnLabel), 'Gesamt', 'Maximum', 'Prozent', ...gradeColumns]);

  // Max-points row
  rows.push([
    'Max. Punkte',
    ...columns.map(columnMax),
    String(roundToOne(summary.totalPossiblePoints)),
    String(roundToOne(summary.totalPossiblePoints)),
    '100%',
    ...gradeColumns.map(() => ''),
  ]);

  // Per-student rows
  for (const s of summary.studentSummaries) {
    rows.push([
      s.name,
      ...columns.map(c =>
        c.kind === 'fehlerindex'
          ? s.fiFehlerindex == null
            ? ''
            : String(roundToOne(s.fiFehlerindex))
          : String(roundToOne(languageUseColumnEarnedPoints(c, s))),
      ),
      String(roundToOne(s.totalPoints)),
      String(roundToOne(s.totalMaxPoints)),
      `${roundToOne(s.percentage)}%`,
      ...(summary.hasFiApplied ? [s.baseGrade, s.grade] : [s.grade]),
    ]);
  }

  // Average row
  const averageGradeText = summary.averageGrade != null ? String(summary.averageGrade) : '';
  rows.push([
    'Durchschnitt',
    ...columns.map(c => {
      if (c.kind === 'fehlerindex') return '';
      if (!summary.studentSummaries.length) return '0';
      const avg =
        summary.studentSummaries.reduce((sum, s) => sum + languageUseColumnEarnedPoints(c, s), 0) /
        summary.studentSummaries.length;
      return String(roundToOne(avg));
    }),
    String(roundToOne(summary.averageTotalPoints)),
    String(roundToOne(summary.totalPossiblePoints)),
    `${roundToOne(summary.averagePercentage)}%`,
    // Only "Note mit FI" (the grade Notenschnitt already averages) gets an average shown — there's no
    // "Ø Note ohne FI" concept being asked for, so that cell stays blank, same as before this feature.
    ...(summary.hasFiApplied ? ['', averageGradeText] : [averageGradeText]),
  ]);

  // BOM so Excel opens UTF-8 correctly
  const csv = rows.map(row => row.map(esc).join(';')).join('\r\n');
  return Buffer.from('﻿' + csv, 'utf-8');
}

export const generateGradebookCsvMutation = registerMutation({
  operationName: 'generateGradebookCsvMutation',
  type: () => String,
  params: [{ name: 'examId', type: () => String }],
  resolve: (examId: string) => async () => {
    const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 });
    const currentUserId = currentUser?._id;
    if (!currentUserId) throw new Error('Nicht eingeloggt.');

    const examCollection = await getCollection(Exam);
    const submissionCollection = await getCollection(Submission);

    const exam = await examCollection.findOne({ _id: examId });
    if (!exam) throw new Error('Klausur wurde nicht gefunden.');
    if (exam.userID && exam.userID !== currentUserId) throw new Error('Keine Berechtigung für diese Klausur.');

    const submissions = await submissionCollection.find({ examId }).sort({ submittedAt: 1, _id: 1 }).toArray();

    const csvBuffer = buildTeacherSummaryCsv({ exam, submissions: submissions as any[] });
    const zipDate = new Date().toISOString().slice(0, 10);
    const csvFilename = `notenuebersicht_${zipDate}.csv`;

    const file = await AdornisFile.createFromBuffer(csvBuffer, csvFilename, {
      userID: String(currentUserId),
      examId: String(examId),
      fileType: 'gradebook-csv',
    });

    if (!file._id) throw new Error('CSV konnte nicht gespeichert werden.');
    return file._id;
  },
});

export const generateGradingReportsMutation = registerMutation({
  operationName: 'generateGradingReportsMutation',
  type: () => [String],
  params: [{ name: 'examId', type: () => String }],
  resolve: (examId: string) => async () => {
    const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 });
    const currentUserId = currentUser?._id;
    if (!currentUserId) {
      throw new Error('Nicht eingeloggt.');
    }

    const examCollection = await getCollection(Exam);
    const submissionCollection = await getCollection(Submission);

    const exam = await examCollection.findOne({ _id: examId });
    if (!exam) {
      throw new Error('Klausur wurde nicht gefunden.');
    }

    if (exam.userID && exam.userID !== currentUserId) {
      throw new Error('Keine Berechtigung für diese Klausur.');
    }

    const submissions = await submissionCollection.find({ examId }).sort({ submittedAt: 1, _id: 1 }).toArray();

    if (submissions.length === 0) {
      return [];
    }

    const teacherSummary = createTeacherSummary(exam, submissions as any[]);
    const isAnySubmissionNotReady = teacherSummary.studentSummaries.some(
      student =>
        teacherSummary.activeTaskDefinitions.some(taskDef => student.isApprovedByTask[taskDef.taskKey] !== true) ||
        student.hasOpenLanguageUseClarification,
    );

    if (isAnySubmissionNotReady) {
      throw new Error(
        `PDF-Reports können erst erstellt werden, wenn es keine offenen KI-Unsicherheiten mehr gibt (unter ${Math.round(GRADING_THRESHOLDS.MEDIUM_CONFIDENCE * 100)}% ohne manuelle Freigabe).`,
      );
    }

    const generatedReportIds: string[] = [];
    const zip = new JSZip();
    const reportFolder = zip.folder('reports');

    for (let index = 0; index < submissions.length; index += 1) {
      const submission = submissions[index];
      if (!submission) continue;

      const pdfBytes = await buildSubmissionReportPDFBytes({
        exam,
        submission,
        submissionIndex: index,
      });

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const studentIdentifier = normalizeText(submission.studentId) || `submission-${index + 1}`;
      const filename = `grading-report-${studentIdentifier}-${timestamp}.pdf`;
      const pdfBinary = Buffer.from(pdfBytes);
      const createdFile = await PDFFile.createFromBuffer(pdfBinary, filename, {
        userID: String(currentUserId),
        examId: String(examId),
        submissionId: String(submission._id),
      });
      const reportPdfId = createdFile._id;

      if (!reportPdfId) {
        throw new Error('PDF report could not be persisted.');
      }

      generatedReportIds.push(reportPdfId);
      reportFolder?.file(filename, pdfBinary);

      await submissionCollection.updateOne(
        { _id: submission._id },
        {
          $set: {
            gradingReportPdfId: reportPdfId,
            gradingReportGeneratedAt: new Date().toISOString(),
          },
        },
      );
    }

    if (generatedReportIds.length === 0) {
      return [];
    }

    const zipDate = new Date().toISOString().slice(0, 10);
    const teacherReportFilename = `teacher_report_${zipDate}.pdf`;
    const teacherReportBytes = await buildTeacherSummaryPDFBytes({ exam, submissions });
    zip.file(teacherReportFilename, Buffer.from(teacherReportBytes));

    const csvFilename = `notenuebersicht_${zipDate}.csv`;
    zip.file(csvFilename, buildTeacherSummaryCsv({ exam, submissions: submissions as any[] }));

    const zipBuffer = await zip.generateAsync({
      type: 'nodebuffer',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 },
    });

    const zipFilename = `student_report_${zipDate}.zip`;
    const zipFile = await AdornisFile.createFromBuffer(zipBuffer, zipFilename, {
      userID: String(currentUserId),
      examId: String(examId),
      fileType: 'grading-report-zip',
    });

    if (!zipFile._id) {
      throw new Error('ZIP report could not be persisted.');
    }

    return [zipFile._id];
  },
});
